/**
 * STEP — Root App Component
 * Sets up routing with role-based guards.
 */
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { Suspense, useEffect, useState, useRef } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import { useAuthStore } from "@store/authStore";
import { useCurrencyStore } from "@/store/currencyStore";
import { Toaster } from "@/components/ui/toaster";
import { ConfirmHost } from "@/components/ui/confirm";
import LoadingScreen from "@/components/shared/LoadingScreen";
import AppShellSkeleton from "@/components/shared/AppShellSkeleton";
import FeatureGate from "@/components/shared/FeatureGate";
import ModuleRoute from "@/components/shared/ModuleRoute";
import SuspendedWall from "@/components/shared/SuspendedWall";
import { isElectron } from "@/lib/electron";
import { isAppSubdomain, getFullTenantUrl, getTenantSlug } from "@/lib/tenant";

// --- Layouts ---
const DashboardLayout = lazyRetry(() => import("@/layouts/DashboardLayout"));
const AuthLayout = lazyRetry(() => import("@/layouts/AuthLayout"));

// --- Auth Pages ---
const LoginPage = lazyRetry(() => import("@/pages/auth/LoginPage"));
const RegisterPage = lazyRetry(() => import("@/pages/auth/RegisterPage"));

// --- Dashboard Pages ---
const DashboardPage = lazyRetry(() => import("@/pages/dashboard/DashboardPage"));
const POSPage = lazyRetry(() => import("@/pages/pos/POSPage"));
const SalesPage = lazyRetry(() => import("@/pages/sales/SalesPage"));
const ProductsPage = lazyRetry(() => import("@/pages/products/ProductsPageNew"));
const ProductFormPage = lazyRetry(() => import("@/pages/products/ProductFormPage"));
const ProductDetailPage = lazyRetry(() => import("@/pages/products/ProductDetailPage"));
const SaleDetailPage = lazyRetry(() => import("@/pages/sales/SaleDetailPage"));
const SalesLedgerPage = lazyRetry(() => import("@/pages/sales/SalesLedgerPage"));
const CategoryManagementPage = lazyRetry(() => import("@/pages/products/CategoryManagementPage"));
const BrandManagementPage = lazyRetry(() => import("@/pages/products/BrandManagementPage"));
const LabelPrinterPage = lazyRetry(() => import("@/pages/products/LabelPrinterPage"));
const PriceListPage = lazyRetry(() => import("@/pages/products/PriceListPage"));
const ReceiptBuilderPage = lazyRetry(() => import("@/pages/settings/ReceiptBuilderPage"));
const CustomersPage = lazyRetry(() => import("@/pages/customers/CustomersPage"));
const CustomerFormPage = lazyRetry(() => import("@/pages/customers/CustomerFormPage"));
const CustomerDetailPage = lazyRetry(() => import("@/pages/customers/CustomerDetailPage"));
const StaffPage = lazyRetry(() => import("@/pages/staff/StaffPage"));
const StaffFormPage = lazyRetry(() => import("@/pages/staff/StaffFormPage"));
const StoresPage = lazyRetry(() => import("@/pages/stores/StoresPage"));
const InventoryPage = lazyRetry(() => import("@/pages/inventory/InventoryPage"));
const BulkImportPage = lazyRetry(() => import("@/pages/products/BulkImportPage"));
const NewAuditPage = lazyRetry(() => import("@/pages/inventory/NewAuditPage"));
const AuditDetailPage = lazyRetry(() => import("@/pages/inventory/AuditDetailPage"));
const TransfersPage = lazyRetry(() => import("@/pages/inventory/TransfersPage"));
const ShiftDetailPage = lazyRetry(() => import("@/pages/staff/ShiftDetailPage"));
const NewPurchasePage = lazyRetry(() => import("@/pages/purchases/NewPurchasePage"));
const PurchasesPage = lazyRetry(() => import("@/pages/purchases/PurchasesPage"));
const PurchaseDetailPage = lazyRetry(() => import("@/pages/purchases/PurchaseDetailPage"));
const Supplier360Page = lazyRetry(() => import("@/pages/purchases/Supplier360Page"));
const FinancePage = lazyRetry(() => import("@/pages/finance/FinancePage"));
const RentAssetsPage = lazyRetry(() => import("@/pages/rent/RentAssetsPage"));
const RentAssetDetailPage = lazyRetry(() => import("@/pages/rent/RentAssetDetailPage"));
const RentCalendarPage = lazyRetry(() => import("@/pages/rent/RentCalendarPage"));
const RentAnalyticsPage = lazyRetry(() => import("@/pages/rent/RentAnalyticsPage"));
const RentFinancePage = lazyRetry(() => import("@/pages/rent/RentFinancePage"));
const RentBookingsPage = lazyRetry(() => import("@/pages/rent/RentBookingsPage"));
const RentBookingCreatePage = lazyRetry(() => import("@/pages/rent/RentBookingCreatePage"));
const RentBookingDetailPage = lazyRetry(() => import("@/pages/rent/RentBookingDetailPage"));
const RentAssetFormPage = lazyRetry(() => import("@/pages/rent/RentAssetFormPage"));
const RentSettingsPage = lazyRetry(() => import("@/pages/rent/RentSettingsPage"));
const AnalyticsPage = lazyRetry(() => import("@/pages/analytics/AnalyticsPage"));
const SettingsPage = lazyRetry(() => import("@/pages/settings/SettingsPage"));
const SubscriptionPage = lazyRetry(() => import("@/pages/subscription/SubscriptionPage"));
const PaymentReturnPage = lazyRetry(() => import("@/pages/subscription/PaymentReturnPage"));
const SubscriptionOnboardingPage = lazyRetry(() => import("@/pages/onboarding/SubscriptionOnboardingPage"));
const MarketingPage = lazyRetry(() => import("@/pages/marketing/MarketingPage"));
const TelegramPage = lazyRetry(() => import("@/pages/telegram/TelegramPage"));
const ReportsPage = lazyRetry(() => import("@/pages/reports/ReportsPage"));
const ReportBuilderPage = lazyRetry(() => import("@/pages/reports/ReportBuilderPage"));
const IntegrationsPage = lazyRetry(() => import("@/pages/integrations/IntegrationsPage"));
const UzumMarketPage = lazyRetry(() => import("@/pages/integrations/uzum/UzumMarketPage"));
const UzumProductsPage = lazyRetry(() => import("@/pages/integrations/uzum/UzumProductsPage"));
const UzumOrdersPage = lazyRetry(() => import("@/pages/integrations/uzum/UzumOrdersPage"));
const UzumInvoicesPage = lazyRetry(() => import("@/pages/integrations/uzum/UzumInvoicesPage"));
const UzumStockPage = lazyRetry(() => import("@/pages/integrations/uzum/UzumStockPage"));
const UzumFinancePage = lazyRetry(() => import("@/pages/integrations/uzum/UzumFinancePage"));
const UzumReturnsPage = lazyRetry(() => import("@/pages/integrations/uzum/UzumReturnsPage"));
const UzumAutomationPage = lazyRetry(() => import("@/pages/integrations/uzum/UzumAutomationPage"));
const UzumFailedOpsPage = lazyRetry(() => import("@/pages/integrations/uzum/UzumFailedOpsPage"));
const UzumSyncLogsPage = lazyRetry(() => import("@/pages/integrations/uzum/UzumSyncLogsPage"));
const OrdersPage = lazyRetry(() => import("@/pages/orders/OrdersPage"));
const OrderDetailPage = lazyRetry(() => import("@/pages/orders/OrderDetailPage"));
const ExchangePage = lazyRetry(() => import("@/pages/sales/ExchangePage"));
const OnlineShopPage = lazyRetry(() => import("@/pages/online-shop/OnlineShopPage"));
const ThemeCustomizerPage = lazyRetry(() => import("@/pages/online-shop/ThemeCustomizerPage"));
const SupplierReturnsPage = lazyRetry(() => import("@/pages/purchases/SupplierReturnsPage"));
const NewSupplierReturnPage = lazyRetry(() => import("@/pages/purchases/NewSupplierReturnPage"));
const NotFoundPage = lazyRetry(() => import("@/pages/NotFoundPage"));
const ProductCardPage = lazyRetry(() => import("@/pages/products/ProductCardPage"));
const ScanPage = lazyRetry(() => import("@/pages/ScanPage"));
// ScanAnalyticsPage is now the Scans tab inside AnalyticsPage
const SlugFinderPage = lazyRetry(() => import("@/pages/auth/SlugFinderPage"));
const NotificationsPage = lazyRetry(() => import("@/pages/notifications/NotificationsPage"));
const NotificationAnalyticsPage = lazyRetry(() => import("@/pages/notifications/NotificationAnalyticsPage"));
const ActivitiesPage = lazyRetry(() => import("@/pages/activities/ActivitiesPage"));

// ── Bootstrap from cross-domain cookie ──────────────────────────────────────
// After login/register on app.step.uz, a short-lived _step_auth cookie is set
// on the .step.uz domain. This hook reads it on {slug}.step.uz, makes two API
// calls to get the full user+tenant objects, then calls setAuth and clears the
// cookie. This allows seamless auth handoff across subdomains without tokens
// in the URL.
function useBootstrapAuth() {
  const { setAuth, isAuthenticated } = useAuthStore();
  const [bootstrapping, setBootstrapping] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    // Only run on real tenant subdomains, once, and only when not already auth'd
    if (isAppSubdomain() || isAuthenticated || ranRef.current) return;

    const match = document.cookie.match(/(?:^|;)\s*_step_auth=([^;]+)/);
    if (!match) return;

    let payload: { a: string; r: string };
    try {
      payload = JSON.parse(decodeURIComponent(match[1]));
    } catch {
      return;
    }
    if (!payload?.a || !payload?.r) return;

    const slug = getTenantSlug();
    if (!slug) return;

    ranRef.current = true;
    setBootstrapping(true);

    const API = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";
    const hdrs = {
      Authorization: `Bearer ${payload.a}`,
      "X-Tenant-Slug": slug,
      "Content-Type": "application/json",
    };

    // The handoff cookie is consumed only AFTER a successful setAuth, so a
    // transient network failure doesn't strand the user with a cleared cookie.
    const clearHandoffCookie = () => {
      document.cookie = "_step_auth=;domain=.step.uz;path=/;max-age=0;SameSite=Lax";
    };

    const fetchJson = async (path: string) => {
      const r = await fetch(`${API}${path}`, { headers: hdrs });
      if (!r.ok) throw new Error(`${path} → HTTP ${r.status}`);
      return r.json();
    };

    const run = async (attempt = 1): Promise<void> => {
      try {
        const [profileRes, tenantRes] = await Promise.all([
          fetchJson("/auth/profile/"),
          fetchJson("/tenants/me/"),
        ]);
        if (!profileRes.success || !tenantRes.success) {
          throw new Error("Bootstrap response not successful");
        }
        setAuth({
          user:         profileRes.data,
          tenant:       tenantRes.data,
          accessToken:  payload.a,
          refreshToken: payload.r,
        });
        clearHandoffCookie();
      } catch (err) {
        if (attempt < 3) {
          await new Promise((res) => setTimeout(res, 400 * attempt));
          return run(attempt + 1);
        }
        // Exhausted retries — consume the (now likely expired) cookie and fall
        // through to the normal /login redirect instead of looping forever.
        console.error("[bootstrapAuth] cross-domain handoff failed:", err);
        clearHandoffCookie();
      }
    };

    run().finally(() => setBootstrapping(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return bootstrapping;
}

// Handles browsers that have a cached 301 to app.step.uz/p/{slug}/{uuid}.
// Immediately sends them to the correct tenant subdomain where the public
// product card is served without requiring authentication.
function ProductCardRedirect() {
  const { tenantSlug, productUuid } = useParams<{ tenantSlug: string; productUuid: string }>();
  useEffect(() => {
    if (tenantSlug && productUuid) {
      window.location.replace(`https://${tenantSlug}.step.uz/p/${tenantSlug}/${productUuid}`);
    }
  }, [tenantSlug, productUuid]);
  return <LoadingScreen />;
}

// --- Phase 2: app.step.uz auth hub ---
// Shown when user lands on app.step.uz.
//   Authenticated + tenant.slug → set cross-domain cookie, redirect to {slug}.step.uz
//   Not authenticated → show login/register/find-my-store routes
function AppSubdomainRouter() {
  const { isAuthenticated, tenant, accessToken, refreshToken } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated && tenant?.slug && accessToken && refreshToken) {
      // Set a .step.uz-scoped cookie so {slug}.step.uz can auto-log the user in
      const authPayload = JSON.stringify({ a: accessToken, r: refreshToken });
      document.cookie = `_step_auth=${encodeURIComponent(authPayload)};domain=.step.uz;path=/;max-age=60;SameSite=Lax`;
      window.location.replace(getFullTenantUrl(tenant.slug));
    }
  }, [isAuthenticated, tenant, accessToken, refreshToken]);

  if (isAuthenticated && tenant?.slug) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public: redirect cached app.step.uz/p/{slug}/{uuid} bookmarks to the
            correct tenant subdomain where the product card is served without auth. */}
        <Route path="/p/:tenantSlug/:productUuid" element={<ProductCardRedirect />} />

        {/* Auth layout wraps all app.step.uz routes */}
        <Route element={<AuthLayout />}>
          <Route index element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/find" element={<SlugFinderPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

// --- Phase 1: Tenant mismatch guard ---
// If the stored tenant slug doesn't match the URL subdomain, force logout.
// Prevents users from accidentally accessing the wrong business's data.
function TenantGuard() {
  const { isTenantMatch, logout } = useAuthStore();

  useEffect(() => {
    if (!isTenantMatch()) {
      logout();
    }
  }, [isTenantMatch, logout]);

  return null;
}

// --- Guards ---
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return !isAuthenticated ? <>{children}</> : <Navigate to={isElectron() ? "/pos" : "/"} replace />;
}

function ElectronPOSGuard() {
  if (isElectron()) {
    return <Navigate to="/pos" replace />;
  }
  return <Outlet />;
}

function RoleGuard({
  children,
  roles,
}: {
  children: React.ReactNode;
  roles: string[];
}) {
  const user = useAuthStore((s) => s.user);
  if (!user || !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function SuspendedGuard() {
  const tenant = useAuthStore((s) => s.tenant);
  const { pathname } = useLocation();
  if (tenant?.status === "SUSPENDED" && !pathname.startsWith("/subscription")) {
    return <SuspendedWall />;
  }
  return <Outlet />;
}

function OnboardingGuard() {
  const tenant = useAuthStore((s) => s.tenant);
  const { pathname } = useLocation();
  if (
    tenant?.status === "PENDING_SUBSCRIPTION" &&
    !pathname.startsWith("/onboarding")
  ) {
    return <Navigate to="/onboarding/subscription" replace />;
  }
  return <Outlet />;
}

export default function App() {
  // Must be called unconditionally (hooks rule). No-ops on app.step.uz.
  const bootstrapping = useBootstrapAuth();

  // Load the tenant's base-currency symbol once authenticated (single source of
  // truth from finance.CurrencySettings) so all money formatters use it.
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const loadCurrency = useCurrencyStore((s) => s.loadCurrency);
  useEffect(() => {
    if (isAuthed) loadCurrency();
  }, [isAuthed, loadCurrency]);

  // Phase 2: app.step.uz — show login/register/find routes
  if (isAppSubdomain()) {
    return (
      <>
        <AppSubdomainRouter />
        <Toaster />
        <ConfirmHost />
      </>
    );
  }

  // Cross-domain bootstrap in progress — show spinner to prevent auth guards
  // from redirecting to /login before the cookie-based setAuth completes.
  if (bootstrapping) {
    return <LoadingScreen />;
  }

  return (
    <>
      {/* Phase 1: Validates stored tenant matches URL subdomain on every render */}
      <TenantGuard />
      <Suspense fallback={<AppShellSkeleton />}>
        <Routes>
          {/* --- Public Auth Routes --- */}
          <Route
            element={
              <PublicOnlyRoute>
                <AuthLayout />
              </PublicOnlyRoute>
            }
          >
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
          </Route>

          {/* --- Onboarding (PENDING_SUBSCRIPTION) — no sidebar --- */}
          <Route
            path="/onboarding/subscription"
            element={
              <PrivateRoute>
                <SubscriptionOnboardingPage />
              </PrivateRoute>
            }
          />

          {/* --- Protected App Routes --- */}
          <Route
            element={
              <PrivateRoute>
                <OnboardingGuard />
              </PrivateRoute>
            }
          >
            <Route element={<ElectronPOSGuard />}>
            <Route
              element={<DashboardLayout />}
            >
            {/* /subscription always accessible (needed to renew when suspended) */}
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/subscription/payment-return" element={<PaymentReturnPage />} />

            {/* SuspendedGuard: all other routes blocked when status === SUSPENDED */}
            <Route element={<SuspendedGuard />}>
              <Route index element={<DashboardPage />} />
              <Route path="/sales" element={<SalesPage />} />
              <Route path="/sales/ledger" element={<SalesLedgerPage />} />
              <Route path="/sales/:id" element={<SaleDetailPage />} />
              <Route path="/sales/:id/exchange" element={<ExchangePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/new" element={<ProductFormPage />} />
              <Route path="/products/:id/edit" element={<ProductFormPage />} />
              <Route path="/products/categories" element={<CategoryManagementPage />} />
              <Route path="/products/brands" element={<BrandManagementPage />} />
              <Route path="/products/labels" element={<LabelPrinterPage />} />
              <Route path="/products/price-list" element={<PriceListPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/settings/receipt" element={<FeatureGate feature="receipt_builder"><ReceiptBuilderPage /></FeatureGate>} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/products/bulk-import" element={<FeatureGate feature="bulk_import"><BulkImportPage /></FeatureGate>} />
              <Route path="/inventory/transfers" element={<FeatureGate feature="inventory_transfers"><TransfersPage /></FeatureGate>} />
              <Route path="/inventory/audits/new" element={<FeatureGate feature="inventory_audits"><NewAuditPage /></FeatureGate>} />
              <Route path="/inventory/audits/:id" element={<FeatureGate feature="inventory_audits"><AuditDetailPage /></FeatureGate>} />
              <Route path="/customers" element={<ModuleRoute feature="customers"><CustomersPage /></ModuleRoute>} />
              <Route path="/customers/new" element={<CustomerFormPage />} />
              <Route path="/customers/:id" element={<CustomerDetailPage />} />
              <Route path="/customers/:id/edit" element={<CustomerFormPage />} />
              <Route path="/purchases" element={<ModuleRoute feature="purchases"><PurchasesPage /></ModuleRoute>} />
              <Route path="/purchases/new" element={<NewPurchasePage />} />
              <Route path="/purchases/:id" element={<PurchaseDetailPage />} />
              <Route path="/purchases/suppliers/:id" element={<Supplier360Page />} />
              <Route path="/finance" element={<ModuleRoute feature="finance"><FinancePage /></ModuleRoute>} />
              <Route path="/rent" element={<ModuleRoute feature="rent"><RentAssetsPage /></ModuleRoute>} />
              <Route path="/rent/assets" element={<ModuleRoute feature="rent"><RentAssetsPage /></ModuleRoute>} />
              <Route path="/rent/assets/new" element={<ModuleRoute feature="rent"><RentAssetFormPage /></ModuleRoute>} />
              <Route path="/rent/assets/:id/edit" element={<ModuleRoute feature="rent"><RentAssetFormPage /></ModuleRoute>} />
              <Route path="/rent/assets/:id" element={<ModuleRoute feature="rent"><RentAssetDetailPage /></ModuleRoute>} />
              <Route path="/rent/calendar" element={<ModuleRoute feature="rent"><RentCalendarPage /></ModuleRoute>} />
              <Route path="/rent/analytics" element={<ModuleRoute feature="rent"><RentAnalyticsPage /></ModuleRoute>} />
              <Route path="/rent/finance" element={<ModuleRoute feature="rent"><RentFinancePage /></ModuleRoute>} />
              <Route path="/rent/settings" element={<ModuleRoute feature="rent"><RentSettingsPage /></ModuleRoute>} />
              <Route path="/rent/bookings" element={<ModuleRoute feature="rent"><RentBookingsPage /></ModuleRoute>} />
              <Route path="/rent/bookings/new" element={<ModuleRoute feature="rent"><RentBookingCreatePage /></ModuleRoute>} />
              <Route path="/rent/bookings/:id" element={<ModuleRoute feature="rent"><RentBookingDetailPage /></ModuleRoute>} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              {/* /analytics/scans is now the Scans tab inside /analytics */}
              <Route path="/analytics/scans" element={<Navigate to="/analytics?tab=scans" replace />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route
                path="/reports/builder"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><ReportBuilderPage /></RoleGuard>}
              />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/notifications/analytics" element={<NotificationAnalyticsPage />} />
              <Route path="/activities" element={<ModuleRoute feature="activities"><ActivitiesPage /></ModuleRoute>} />
              <Route
                path="/marketing"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><MarketingPage /></RoleGuard>}
              />
              <Route
                path="/telegram"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><ModuleRoute feature="telegram_bot"><TelegramPage /></ModuleRoute></RoleGuard>}
              />
              <Route
                path="/integrations"
                element={<RoleGuard roles={["OWNER"]}><IntegrationsPage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum"
                element={<RoleGuard roles={["OWNER"]}><UzumMarketPage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum/products"
                element={<RoleGuard roles={["OWNER"]}><UzumProductsPage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum/orders"
                element={<RoleGuard roles={["OWNER"]}><UzumOrdersPage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum/invoices"
                element={<RoleGuard roles={["OWNER"]}><UzumInvoicesPage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum/stock"
                element={<RoleGuard roles={["OWNER"]}><UzumStockPage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum/finance"
                element={<RoleGuard roles={["OWNER"]}><UzumFinancePage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum/returns"
                element={<RoleGuard roles={["OWNER"]}><UzumReturnsPage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum/automation"
                element={<RoleGuard roles={["OWNER"]}><UzumAutomationPage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum/failed-ops"
                element={<RoleGuard roles={["OWNER"]}><UzumFailedOpsPage /></RoleGuard>}
              />
              <Route
                path="/integrations/uzum/sync-logs"
                element={<RoleGuard roles={["OWNER"]}><UzumSyncLogsPage /></RoleGuard>}
              />
              <Route
                path="/orders"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><OrdersPage /></RoleGuard>}
              />
              <Route
                path="/orders/:id"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><OrderDetailPage /></RoleGuard>}
              />
              <Route
                path="/online-shop"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><OnlineShopPage /></RoleGuard>}
              />
              <Route
                path="/online-shop/customize"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><ThemeCustomizerPage /></RoleGuard>}
              />
              <Route
                path="/purchases/returns"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><SupplierReturnsPage /></RoleGuard>}
              />
              <Route
                path="/purchases/returns/new"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><NewSupplierReturnPage /></RoleGuard>}
              />
              <Route
                path="/staff"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><ModuleRoute feature="staff"><StaffPage /></ModuleRoute></RoleGuard>}
              />
              <Route
                path="/staff/new"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><StaffFormPage /></RoleGuard>}
              />
              <Route
                path="/staff/:id/edit"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><StaffFormPage /></RoleGuard>}
              />
              <Route path="/staff/shifts/:id" element={<ShiftDetailPage />} />
              <Route
                path="/stores"
                element={<RoleGuard roles={["OWNER", "MANAGER"]}><StoresPage /></RoleGuard>}
              />
            </Route>
            </Route>
            </Route>
          </Route>

          {/* --- POS Terminal (full-screen, separate layout) --- */}
          <Route
            path="/pos"
            element={
              <PrivateRoute>
                <POSPage />
              </PrivateRoute>
            }
          />

          {/* --- Public routes (no auth required) --- */}
          {/* Legacy: step.uz/p/{slug}/{uuid} — kept for backward compat with printed QR codes */}
          <Route path="/p/:tenantSlug/:productUuid" element={<ProductCardPage />} />
          {/* Phase 4: {slug}.step.uz/p/{uuid} — slug comes from hostname */}
          <Route path="/p/:productUuid" element={<ProductCardPage />} />
          <Route path="/scan" element={<ScanPage />} />

          {/* --- 404 --- */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      <Toaster />
      <ConfirmHost />
    </>
  );
}
